import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Editor, useEditor } from "@tiptap/react";
import { KeyboardShortcutCommand } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import HardBreak from "@tiptap/extension-hard-break";
import Check from "@src/assets/icons/check.svg";
import CloseIcon from "@src/assets/icons/CloseIcon/CloseIcon";
import { StyledButton, StyledEditorContent, UserTimestamp } from "./style";

interface RichTextEditorTypes {
  initialHtml?: string;
  onConfirm: (
    content: string,
    sectionId: number,
    entryId: number,
    editorId: string
  ) => void;
  entryId: number;
  sectionId: number;
  onFocus: (richTextEditor: Editor, editorId: string) => void;
  placeholder: string;
  loading: boolean;
  hasActiveEditors: boolean;
  onCancel: (editorId: string) => void;
  viewMode?: boolean;
  updatedLabel?: string;
  editorId: string;
}

const RichTextEditor: FC<RichTextEditorTypes> = ({
  initialHtml = "",
  entryId = 0,
  sectionId,
  onConfirm,
  onFocus,
  placeholder,
  loading,
  viewMode,
  updatedLabel,
  hasActiveEditors,
  onCancel,
  editorId,
}) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isProgrammaticBlur, setIsProgrammaticBlur] = useState(false);
  const handleChange = () => {
    setHasUnsavedChanges(true);
  };
  const extensions = [
    StarterKit.configure({
      hardBreak: false,
      paragraph: {
        HTMLAttributes: {
          class: "tiptap-paragraph",
        },
      },
    }),
    HardBreak.configure({
      keepMarks: false,
      HTMLAttributes: {},
    }).extend({
      addKeyboardShortcuts() {
        return {
          Enter: ({ editor }: Parameters<KeyboardShortcutCommand>[0]) =>
            editor.commands.setHardBreak(),
          "Shift-Enter": ({ editor }: Parameters<KeyboardShortcutCommand>[0]) =>
            editor.commands.setHardBreak(),
        };
      },
    }),
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    Placeholder.configure({
      placeholder,
    }),
  ];
  const richTextEditor = useEditor({
    extensions,
    content: initialHtml,
    onUpdate: handleChange,
    parseOptions: {
      preserveWhitespace: "full",
    },
  });

  const isEditorEmpty = (noteEntryContent: string): boolean => {
    return noteEntryContent.replace(/\s/g, "") === "<p></p>";
  };

  const handleOnBlur = useCallback(() => {
    if (isProgrammaticBlur) {
      setIsProgrammaticBlur(false);
      return;
    }
    if (!hasUnsavedChanges) {
      onCancel(editorId);
    }
  }, [isProgrammaticBlur, hasUnsavedChanges, onCancel, editorId]);

  const allowActionButtons = useMemo(() => {
    return (
      !viewMode && (richTextEditor?.isFocused || hasUnsavedChanges) && !loading
    );
  }, [richTextEditor?.isFocused, loading, hasUnsavedChanges, viewMode]);

  useEffect(() => {
    richTextEditor?.setOptions({
      editable: !viewMode && !loading,
    });
  }, [loading, richTextEditor, viewMode]);

  useEffect(() => {
    if (richTextEditor) {
      richTextEditor.commands.setContent(initialHtml);
    }
  }, [initialHtml]);

  return (
    richTextEditor && (
      <>
        <StyledEditorContent
          editor={richTextEditor}
          onFocus={() => onFocus(richTextEditor, editorId)}
          onBlur={() => handleOnBlur()}
          className={
            hasActiveEditors && !richTextEditor.isFocused && !hasUnsavedChanges
              ? "is-editing"
              : ""
          }
        />
        {updatedLabel && <UserTimestamp>{updatedLabel}</UserTimestamp>}
        {allowActionButtons && (
          <div>
            <StyledButton
              disabled={isEditorEmpty(richTextEditor.getHTML())}
              onMouseDown={(event) => event.preventDefault()}
              variant="contained"
              onClick={() => {
                setHasUnsavedChanges(false);
                if (richTextEditor.getHTML() === initialHtml) {
                  richTextEditor.commands.blur();
                  return;
                }

                onConfirm(
                  richTextEditor.getHTML(),
                  sectionId,
                  entryId || 0,
                  editorId
                );
                richTextEditor.commands.blur();
                if (entryId === 0) {
                  richTextEditor.commands.setContent("");
                }
              }}
              type="button"
            >
              <img src={Check} alt="Confirm changes" width={18} />
            </StyledButton>
            <StyledButton
              onMouseDown={(event) => event.preventDefault()}
              className="cancel"
              onClick={() => {
                richTextEditor.commands.setContent(initialHtml);
                setHasUnsavedChanges(false);
                setIsProgrammaticBlur(true);
                richTextEditor.commands.blur();
                onCancel(editorId);
              }}
              type="button"
            >
              <CloseIcon />
            </StyledButton>
          </div>
        )}
      </>
    )
  );
};

export default RichTextEditor;
